import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Link, Skeleton, Button, Grid, useTheme, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProfileReviews } from '../../services/api';
import { formatReviewText } from '../../utils/formatText';
import { effectivePlayTimeHours } from '../../utils/playtime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DetailScores from '../review/DetailScores';

interface ProfileReview {
  id: number;
  game: {
    id: number;
    name: string;
    cover_id: string | null;
  };
  score: number | null;
  review_text: string;
  play_time: number | null;
  steam_playtime_min: number | null;
  play_start_date: string | null;
  play_end_date: string | null;
  created_at: string;
  score_visual: number | null;
  score_sound: number | null;
  score_gameplay: number | null;
  score_narrative: number | null;
  score_originality: number | null;
}

interface ProfileReviewsWidgetProps {
  username?: string;
  isPublic?: boolean;
}

// 共通のPaperスタイルを定義
const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  position: 'relative',
  mb: 0,
};

// カバー画像のURLを生成する関数
const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

// スコアに応じて色を取得する関数
const getScoreColor = (score: number | null) => {
  if (score === null) return "#666666";
  if (score >= 4.5) return "#4CAF50";
  if (score >= 4.0) return "#8BC34A";
  if (score >= 3.5) return "#CDDC39";
  if (score >= 3.0) return "#FFEB3B";
  if (score >= 2.5) return "#FFC107";
  if (score >= 2.0) return "#FF9800";
  if (score >= 1.5) return "#FF5722";
  return "#F44336";
};

const formatPlayDate = (date: string | null) => {
  if (date === null) return "";
  const playDate = new Date(date);
  return playDate.toLocaleDateString();
};

const ProfileReviewsWidget: React.FC<ProfileReviewsWidgetProps> = ({ username, isPublic = false }) => {
  const { t } = useTranslation('profile');
  const [reviews, setReviews] = useState<ProfileReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState<{ [key: number]: boolean }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const maxLength = isMobile ? 120 : 180;
  const maxLines = isMobile ? 4 : 5;

  const toggleExpand = (reviewId: number) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: true
    }));
  };

  const truncateText = (text: string) => {
    const lines = text.split('\n');
    if (lines.length > maxLines || text.length > maxLength) {
      const truncatedLines = lines.slice(0, maxLines);
      let truncatedText = truncatedLines.join('\n');
      if (truncatedText.length > maxLength) {
        truncatedText = truncatedText.slice(0, maxLength);
      }
      return `${truncatedText}...`;
    }
    return text;
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await getProfileReviews(username);
        setReviews(response.data);
      } catch (error) {
        console.error('プロフィールレビューの取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [username]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          {t('widgets.reviews.titleRecent')}
        </Typography>
        <Grid container spacing={{ xs: 0, sm: 2 }}>
          {[...Array(2)].map((_, index) => (
            <Grid item xs={12} sm={12} key={index}>
              <Box sx={{ mb: { xs: index === 0 ? 2 : 0, sm: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <Skeleton variant="rectangular" width={60} height={80} sx={{ mr: 1.5, borderRadius: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (reviews.length === 0) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          {username ? t('widgets.reviews.titleRecentUser', { username }) : t('widgets.reviews.titleRecent')}
        </Typography>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('widgets.reviews.empty')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 1.5, sm: 2.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" lineHeight={1}>
          {t('widgets.reviews.title')}
        </Typography>
        {reviews.length > 0 && (
          <Button
            component={RouterLink}
            to={username ? `/profile/${username}?tab=reviews` : '/profile?tab=reviews'}
            size="small"
            variant="text"
            sx={{
              fontSize: '0.7rem',
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {t('widgets.reviews.viewAll')}
          </Button>
        )}
      </Box>

      <Grid container spacing={{ xs: 0, sm: 2 }}>
        {reviews.slice(0, 3).map((review, index) => (
          <Grid item xs={12} sm={12} key={review.id}>
            <Box
              sx={{
                mb: { xs: index === reviews.slice(0, 3).length - 1 ? 0 : 2, sm: index === reviews.slice(0, 3).length - 1 ? 0 : 0.5 },
                pb: { xs: index === reviews.slice(0, 3).length - 1 ? 0 : 1, sm: index === reviews.slice(0, 3).length - 1 ? 0 : 0.5 },
                borderBottom: {
                  xs: index === reviews.slice(0, 3).length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
                  sm: index === reviews.slice(0, 3).length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.08)'
                },
                height: { sm: '100%' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  component={RouterLink}
                  to={`/game/${review.game.id}`}
                  sx={{
                    display: 'block',
                    width: 60,
                    mr: 1.5,
                    flexShrink: 0
                  }}
                >
                  <Box
                    component="img"
                    src={getCoverUrl(review.game.cover_id)}
                    alt={review.game.name}
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      objectFit: 'cover',
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Link
                    component={RouterLink}
                    to={`/game/${review.game.id}`}
                    color="inherit"
                    underline="hover"
                    sx={{
                      typography: 'body2',
                      fontWeight: 500,
                      display: 'block',
                      mb: 0.5,
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {review.game.name}
                  </Link>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontSize: '0.75rem',
                          py: 0.2,
                          px: 0.6,
                          lineHeight: '1',
                          borderRadius: 0.5,
                          backgroundColor: `${getScoreColor(review.score)}B3`,
                          minWidth: '24px',
                          textAlign: 'center'
                        }}
                      >
                        {review.score !== null ? review.score.toFixed(1) : '-'}
                      </Typography>

                      <Box
                        sx={{
                          width: 40,
                          height: 3,
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: review.score !== null ? `${((review.score - 1) / 4) * 100}%` : '100%',
                            height: '100%',
                            bgcolor: getScoreColor(review.score),
                            borderRadius: 1.5,
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem' }}>
                      {effectivePlayTimeHours(review.play_time, review.steam_playtime_min) ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            fontSize: '0.6rem'
                          }}
                        >
                          <TimerOutlinedIcon sx={{ mr: 0.25, fontSize: '0.7rem' }} />
                          {effectivePlayTimeHours(review.play_time, review.steam_playtime_min)}h
                        </Typography>
                      ) : null}

                      {(review.play_start_date || review.play_end_date) && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            fontSize: '0.6rem'
                          }}
                        >
                          <PlayCircleOutlineIcon sx={{ mr: 0.25, fontSize: '0.7rem' }} />
                          {formatPlayDate(review.play_start_date)}
                          {review.play_end_date && review.play_start_date && ' - '}
                          {review.play_end_date && formatPlayDate(review.play_end_date)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <DetailScores
                    scoreVisual={review.score_visual}
                    scoreSound={review.score_sound}
                    scoreGameplay={review.score_gameplay}
                    scoreNarrative={review.score_narrative}
                    scoreOriginality={review.score_originality}
                    variant="reviewsTab"
                    hideBarOnDesktop={true}
                  />

                  {review.review_text && (
                    <>
                      <Box sx={{ mt: 1, position: 'relative', mb: 1.5 }}>
                        <Typography
                          variant="body2"
                          component="div"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            '& .spoiler': {
                              backgroundColor: '#444',
                              padding: '0 4px',
                              borderRadius: '2px',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'transparent'
                              }
                            }
                          }}
                        >
                          {formatReviewText(
                            expandedReviews[review.id] ? review.review_text : truncateText(review.review_text),
                            isMobile
                          )}
                        </Typography>
                        {(review.review_text.length > maxLength || review.review_text.split('\n').length > maxLines) && !expandedReviews[review.id] && (
                          <Link
                            component="span"
                            onClick={() => toggleExpand(review.id)}
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              cursor: 'pointer',
                              color: 'primary.main',
                              fontSize: '0.7rem',
                              fontWeight: 'normal',
                              lineHeight: '1.5',
                              backgroundColor: 'background.paper',
                              pl: 1,
                            }}
                          >
                            {t('widgets.reviews.readMore')}
                          </Link>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {new Date(review.created_at).toLocaleDateString()}
                        </Typography>
                        <Link
                          component={RouterLink}
                          to={`/game/${review.game.id}/reviews/${review.id}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.6rem',
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                        </Link>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default ProfileReviewsWidget;